/** Parse user lookup: phone / email / name + role (pure). */

export type SlateUserKind =
    | 'VIEWER'
    | 'FILM_MAKER'
    | 'ADMIN'
    | 'AGENT'
    | 'CONTENT_CURATOR'
    | 'ANY';

export type SlateUserQueryKind = 'phone' | 'email' | 'name';

export interface SlateUserLookupCommand {
    query: string;
    queryKind: SlateUserQueryKind;
    userType: SlateUserKind;
    open: boolean;
    openMode: 'view' | 'edit';
}

export function isUserLookupCommand(text: string): boolean {
    const t = text.toLowerCase().trim();
    if (!t) return false;
    if (
        /how\s+(?:do\s+i|to|can\s+i)|where\s+(?:is|can|do)|steps?\s+to|guide\s+me/.test(
            t
        )
    ) {
        return false;
    }
    // Content filmmaker-of-title — not a user profile Q
    if (
        /\b(?:film\s*makers?|filmmakers?|maker|producer|director)\s+(?:of|for)\b/.test(
            t
        )
    ) {
        return false;
    }
    if (/\bwho\s+(?:made|uploaded|produced)\b/.test(t)) return false;

    // Pure list intents stay on user_list
    if (
        /^(?:list|show|get)\s+(?:all\s+)?(?:filmmakers?|viewers?|admins?|channel\s*partners?|content\s*curators?|agents?)\b/.test(
            t
        ) &&
        !extractPhone(t) &&
        !extractEmail(text)
    ) {
        return false;
    }

    if (extractPhone(t) || extractEmail(text)) return true;

    // "Who are you?" / "Who am I?" — not a directory search
    if (
        /\bwho\s+(?:are|is)\s+(?:you|talkie|slate|this|ai|assistant)\b/.test(t) ||
        /\bwho\s+am\s+i\b/.test(t)
    ) {
        return false;
    }

    // "Who is Prashant?" / "Who is Jane Doe?" — name profile (not filmmaker-of-title)
    if (
        /\bwho\s+(?:is|are|was)\s+/.test(t) &&
        !/\bwho\s+(?:is|are|was)\s+(?:the\s+)?(?:film\s*makers?|filmmakers?|maker|producer|director)\b/.test(
            t
        ) &&
        !/\b(?:film\s*makers?|filmmakers?|maker|producer|director)\s+(?:of|for)\b/.test(
            t
        ) &&
        !/\b(?:made|uploaded|produced)\b/.test(t) &&
        !/\b(?:film|movie|web\s*series|series|status|views?|revenue|purchases?)\b/.test(
            t
        )
    ) {
        return true;
    }

    if (
        /\b(?:find|search|look\s*up|lookup|who\s+is|identify|which\s+user|user\s+with)\b/.test(
            t
        ) &&
        (/\b(?:viewer|film\s*makers?|filmmakers?|admin|channel\s*partners?|content\s*curators?|agents?|user)\b/.test(
            t
        ) ||
            /\b(?:mobile|phone|number|email)\b/.test(t))
    ) {
        return true;
    }

    if (
        /\b(?:open|go\s+to|navigate\s+to)\s+(?:the\s+)?(?:viewer|film\s*makers?|filmmakers?|admin|channel\s*partners?|content\s*curators?|agents?|user)\b/.test(
            t
        )
    ) {
        return true;
    }

    if (
        /\b(?:viewer|film\s*makers?|filmmakers?|admin|channel\s*partners?|content\s*curators?|agents?)\b/.test(
            t
        ) &&
        /\b(?:named|called|with\s+name|details?|profile|info)\b/.test(t)
    ) {
        return true;
    }

    return false;
}

export function extractUserLookup(
    text: string
): SlateUserLookupCommand | null {
    if (!isUserLookupCommand(text)) return null;

    const phone = extractPhone(text);
    const email = extractEmail(text);
    let queryKind: SlateUserQueryKind = 'name';
    let query = '';

    if (phone) {
        queryKind = 'phone';
        query = phone;
    } else if (email) {
        queryKind = 'email';
        query = email;
    } else {
        query = extractUserName(text);
        queryKind = 'name';
    }

    if (!query) return null;

    const userType = detectUserType(text);
    const open = /\b(?:open|go\s+to|navigate\s+to|take\s+me)\b/i.test(text);
    let openMode: 'view' | 'edit' = 'view';
    if (/\bedit\s+mode\b|\bin\s+edit\b/.test(text.toLowerCase())) {
        openMode = 'edit';
    }

    return { query, queryKind, userType, open, openMode };
}

export function normalizePhoneDigits(raw: string): string {
    return String(raw || '').replace(/\D/g, '');
}

function extractPhone(text: string): string | undefined {
    // Prefer +cc patterns, then long digit runs
    const plus = text.match(
        /(?:\+|00)?\s*\d{1,3}[\s-]?\d{6,12}|\b\d{10,15}\b/
    );
    if (!plus?.[0]) return undefined;
    const digits = normalizePhoneDigits(plus[0]);
    if (digits.length < 8 || digits.length > 15) return undefined;
    // Avoid matching pure years / tiny numbers
    if (/^(19|20)\d{2}$/.test(digits)) return undefined;
    return digits;
}

function extractEmail(text: string): string | undefined {
    const m = text.match(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/);
    return m?.[0]?.toLowerCase();
}

function detectUserType(text: string): SlateUserKind {
    const t = text.toLowerCase();
    if (/\bcontent\s*curators?\b|\bcurators?\b/.test(t)) {
        return 'CONTENT_CURATOR';
    }
    if (/\bchannel\s*partners?\b|\bagents?\b/.test(t)) return 'AGENT';
    if (/\badmins?\b/.test(t) && !/\bfilmmakers?\b/.test(t)) return 'ADMIN';
    if (/\bviewers?\b/.test(t)) return 'VIEWER';
    if (/\bfilm\s*makers?\b|\bfilmmakers?\b/.test(t)) return 'FILM_MAKER';
    return 'ANY';
}

function extractUserName(raw: string): string | undefined {
    const quoted = raw.match(/["']([^"']{1,120})["']/);
    if (quoted?.[1]) return cleanUserName(quoted[1]);

    let m = raw.match(
        /\b(?:named|called|name)\s+([a-zA-Z][a-zA-Z .'-]{1,80})(?:\?|$)/i
    );
    if (m?.[1]) return cleanUserName(m[1]);

    m = raw.match(
        /\b(?:open|find|search|look\s*up|lookup|who\s+is)\s+(?:the\s+)?(?:viewer|film\s*makers?|filmmakers?|admin|channel\s*partners?|content\s*curators?|agents?|user)\s+(.+?)(?:\?|$)/i
    );
    if (m?.[1]) return cleanUserName(m[1]);

    m = raw.match(
        /\b(?:viewer|film\s*makers?|filmmakers?|admin|channel\s*partners?|content\s*curators?|agents?)\s+(.+?)(?:\s+profile|\s+details?|\?|$)/i
    );
    if (m?.[1]) return cleanUserName(m[1]);

    // Who is Prashant? / Who was Jane Doe?
    m = raw.match(/\bwho\s+(?:is|are|was)\s+(.+?)(?:\?|$)/i);
    if (m?.[1]) {
        const cand = m[1].trim();
        if (
            /^(?:the\s+)?(?:film\s*makers?|filmmakers?|maker|producer|director)\b/i.test(
                cand
            )
        ) {
            return undefined;
        }
        if (/\b(?:mobile|phone|number|email)\b/i.test(cand)) {
            return undefined;
        }
        return cleanUserName(cand);
    }

    return undefined;
}

function cleanUserName(s: string): string | undefined {
    let cleaned = s
        .replace(/["']/g, '')
        .replace(/^(?:please|the|a|an)\s+/i, '')
        .replace(/\s+/g, ' ')
        .trim();
    cleaned = cleaned
        .replace(
            /^(?:viewer|film\s*makers?|filmmakers?|admin|channel\s*partners?|content\s*curators?|agents?|user)\s+/i,
            ''
        )
        .trim();
    cleaned = cleaned
        .replace(/\s+(?:in\s+)?(?:view|edit)(?:\s+mode)?$/i, '')
        .trim();
    cleaned = cleaned.replace(/\?+$/, '').trim();
    if (cleaned.length < 2) return undefined;
    if (
        /^(last|this|previous|today|with|mobile|phone|number|email|profile|details?|you|me|i|we|they|them|someone|anyone|everyone|admin|admins|viewer|viewers|filmmaker|filmmakers|user|users|agent|agents|curator|curators|partner|partners)$/i.test(
            cleaned
        )
    ) {
        return undefined;
    }
    return cleaned;
}
